var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useContext } from 'react';
import { isEmpty } from 'lodash';
import ThemeProvider from '../../../context/ThemeContext';
import ImageContent from '../../../../ImageContent/ImageContent';
import { contentCardSizeMappings } from '../Card.utils';
import * as Styled from './Content.styled';
import { ContainerQueryContext } from '../../../../../util/ContainerQuery.context';
var DEFAULT_IMAGE_RESOLUTIONS = {
    small: 432,
    medium: 448,
    large: 516,
    xlarge: 670,
    largest: 1520
};
var assignElementToCollection = function (element, position, elementsCollections, hasImage) {
    if (!element.content || isEmpty(element.content))
        return;
    if (position === 'ABOVE_IMAGE') {
        if (hasImage) {
            elementsCollections.aboveImage.push(element);
        }
        else {
            elementsCollections.withinImageTop.push(__assign(__assign({}, element), { position: 'TOP' }));
        }
    }
    else if (position === 'BELOW_IMAGE') {
        if (hasImage) {
            elementsCollections.belowImage.push(element);
        }
        else {
            elementsCollections.withinImageBottom.push(element);
        }
    }
    else if (position === 'TOP' || !element.position) {
        elementsCollections.withinImageTop.push(element);
    }
    else if (position === 'MIDDLE') {
        elementsCollections.withinImageMiddle.push(element);
    }
    else if (position === 'BOTTOM') {
        elementsCollections.withinImageBottom.push(element);
    }
    else {
        elementsCollections.withinImageTop.push(element);
    }
};
var dynamicPositions = [
    'ABOVE_IMAGE',
    'BELOW_IMAGE',
    'ABOVE_PRIMARY_HEADER',
    'BELOW_PRIMARY_HEADER',
    'BELOW_HEADERS',
    'BELOW_SUBHEADER',
    'ABOVE_HEADERS'
];
var getOrderedElements = function (elements) {
    // Separate static and dynamic positioned elements
    var topElements = [];
    var middleElements = [];
    var bottomElements = [];
    var dynamicElements = [];
    elements.forEach(function (element) {
        if (element.position && dynamicPositions.includes(element.position)) {
            dynamicElements.push(element);
        }
        else if (element.position === 'MIDDLE') {
            middleElements.push(element);
        }
        else if (element.position === 'BOTTOM') {
            bottomElements.push(element);
        }
        else {
            // Default to TOP for TOP, ABOVE_IMAGE, BELOW_IMAGE, or undefined positions
            topElements.push(element);
        }
    });
    // Sort each group by hierarchy: Icon, Primary Header, Secondary Header, Content, CTA
    var hierarchy = [
        'icon',
        'primaryHeader',
        'secondaryHeader',
        'subheader',
        'cta'
    ];
    var sortByHierarchy = function (elms) {
        return elms.sort(function (a, b) { return hierarchy.indexOf(a.type) - hierarchy.indexOf(b.type); });
    };
    sortByHierarchy(topElements);
    sortByHierarchy(middleElements);
    sortByHierarchy(bottomElements);
    // Combine in order: TOP, MIDDLE, BOTTOM
    var orderedElements = __spreadArray(__spreadArray(__spreadArray([], topElements, true), middleElements, true), bottomElements, true);
    // Insert dynamic elements at correct positions
    dynamicPositions.forEach(function (position) {
        var elementsAtPosition = dynamicElements.filter(function (el) { return el.position === position; });
        if (elementsAtPosition.length === 0)
            return;
        var insertIndex = 0;
        switch (position) {
            case 'ABOVE_PRIMARY_HEADER':
                insertIndex = orderedElements.findIndex(function (el) { return el.type === 'primaryHeader'; });
                if (insertIndex === -1)
                    insertIndex = 0;
                break;
            case 'BELOW_PRIMARY_HEADER':
                insertIndex = orderedElements.findIndex(function (el) { return el.type === 'primaryHeader'; });
                insertIndex =
                    insertIndex === -1 ? orderedElements.length : insertIndex + 1;
                break;
            case 'ABOVE_HEADERS': {
                var firstHeaderIndex = Math.min.apply(Math, [
                    orderedElements.findIndex(function (el) { return el.type === 'primaryHeader'; }),
                    orderedElements.findIndex(function (el) { return el.type === 'secondaryHeader'; })
                ].filter(function (idx) { return idx !== -1; }));
                insertIndex = firstHeaderIndex === Infinity ? 0 : firstHeaderIndex;
                break;
            }
            case 'BELOW_HEADERS': {
                var lastHeaderIndex = Math.max.apply(Math, [
                    orderedElements.findIndex(function (el) { return el.type === 'primaryHeader'; }),
                    orderedElements.findIndex(function (el) { return el.type === 'secondaryHeader'; })
                ].filter(function (idx) { return idx !== -1; }));
                insertIndex =
                    lastHeaderIndex === -Infinity
                        ? orderedElements.length
                        : lastHeaderIndex + 1;
                break;
            }
            case 'BELOW_SUBHEADER':
                insertIndex = orderedElements.findIndex(function (el) { return el.type === 'subheader'; });
                insertIndex =
                    insertIndex === -1 ? orderedElements.length : insertIndex + 1;
                break;
            case 'BELOW_IMAGE':
                insertIndex = orderedElements.length;
                break;
            case 'ABOVE_IMAGE':
                insertIndex = 0;
                break;
            default:
                insertIndex = orderedElements.length;
        }
        orderedElements.splice.apply(orderedElements, __spreadArray([insertIndex, 0], elementsAtPosition, false));
    });
    return orderedElements;
};
var TextSetContent = function (_a) {
    var elements = _a.elements, verticalAlignment = _a.verticalAlignment, _b = _a.inImage, inImage = _b === void 0 ? false : _b, backgroundColor = _a.backgroundColor, sectionType = _a.sectionType, customBackgroundColor = _a.customBackgroundColor, headerMaxLineLength = _a.headerMaxLineLength, horizontalTextAlignment = _a.horizontalTextAlignment, hoverEffect = _a.hoverEffect, internalSpace = _a.internalSpace, isTransparent = _a.isTransparent, responsiveTextSizes = _a.responsiveTextSizes, cardTextColor = _a.cardTextColor, _c = _a.invertImageTextColor, invertImageTextColor = _c === void 0 ? false : _c, styleQueryMode = _a.styleQueryMode, ctaLayout = _a.ctaLayout;
    // Convert elements array to the structure expected by CardTextSet
    var contentObj = elements.reduce(function (obj, element) {
        var newObj = __assign({}, obj);
        switch (element.type) {
            case 'icon':
                newObj.icon = element.content;
                break;
            case 'primaryHeader':
                newObj.header = element.content;
                break;
            case 'secondaryHeader':
                newObj.secondaryHeader = element.content;
                break;
            case 'subheader':
                newObj.body = element.content;
                break;
            case 'cta':
                newObj.ctas = element.content;
                break;
            default:
                break;
        }
        return newObj;
    }, {});
    var orderedElements = getOrderedElements(elements);
    return (React.createElement(Styled.CardTextSet, { backgroundColor: backgroundColor, cardType: sectionType, orderedElements: orderedElements, content: contentObj, ctaLayout: ctaLayout, customBackgroundColor: customBackgroundColor, hasImage: inImage, headerMaxLineLength: headerMaxLineLength, horizontalTextAlignment: horizontalTextAlignment, hoverEffect: hoverEffect, internalSpace: internalSpace, isTransparent: isTransparent || backgroundColor === 'transparent', responsiveSizes: responsiveTextSizes, textColor: cardTextColor, invertImageTextColor: invertImageTextColor, verticalTextPosition: verticalAlignment, "$styleQueryMode": styleQueryMode }));
};
var Content = function (_a) {
    var _b;
    var _c = _a.backgroundColor, backgroundColor = _c === void 0 ? undefined : _c, _d = _a.cardCtas, cardCtas = _d === void 0 ? [] : _d, _e = _a.cardTextColor, cardTextColor = _e === void 0 ? 'AUTO' : _e, invertImageTextColor = _a.invertImageTextColor, columnSpan = _a.columnSpan, ctaLayout = _a.ctaLayout, _f = _a.customBackgroundColor, customBackgroundColor = _f === void 0 ? '' : _f, header = _a.header, headerMaxLineLength = _a.headerMaxLineLength, horizontalTextAlignment = _a.horizontalTextAlignment, hoverEffect = _a.hoverEffect, icon = _a.icon, image = _a.image, _g = _a.imageResolutions, imageResolutions = _g === void 0 ? DEFAULT_IMAGE_RESOLUTIONS : _g, internalSpace = _a.internalSpace, _h = _a.isTransparent, isTransparent = _h === void 0 ? false : _h, responsiveTextSizes = _a.responsiveTextSizes, subheader = _a.subheader, screenSize = _a.screenSize, settings = _a.settings, _j = _a.primaryHeaderPosition, primaryHeaderPosition = _j === void 0 ? 'TOP' : _j, secondaryHeader = _a.secondaryHeader, secondaryHeaderPosition = _a.secondaryHeaderPosition, subheaderPosition = _a.subheaderPosition, ctaPosition = _a.ctaPosition, iconPosition = _a.iconPosition;
    var styleQueryMode = useContext(ContainerQueryContext).styleQueryMode;
    var hasImage = !!image;
    var textSize = (_b = responsiveTextSizes.find(function (size) { return size.screenSize === screenSize; })) === null || _b === void 0 ? void 0 : _b.textSize;
    var sectionType = contentCardSizeMappings[textSize];
    // Determine which elements should be rendered above/within/below image
    var getContentByPosition = function () {
        var elementsCollections = {
            aboveImage: [],
            withinImageTop: [],
            withinImageMiddle: [],
            withinImageBottom: [],
            belowImage: []
        };
        var elements = [
            { type: 'icon', position: iconPosition, content: icon },
            {
                type: 'primaryHeader',
                position: primaryHeaderPosition,
                content: header
            },
            {
                type: 'secondaryHeader',
                position: secondaryHeaderPosition,
                content: secondaryHeader
            },
            {
                type: 'subheader',
                position: subheaderPosition,
                content: subheader
            },
            { type: 'cta', position: ctaPosition, content: cardCtas }
        ];
        elements.forEach(function (element) {
            if (element.position === 'ABOVE_PRIMARY_HEADER' ||
                element.position === 'BELOW_PRIMARY_HEADER') {
                var headerElement = elements.find(function (elm) { return elm.type === 'primaryHeader'; });
                if (headerElement && headerElement.position) {
                    assignElementToCollection(element, headerElement.position, elementsCollections, hasImage);
                }
                else {
                    assignElementToCollection(element, element.position, elementsCollections, hasImage);
                }
            }
            else if (element.position === 'ABOVE_HEADERS' ||
                element.position === 'BELOW_HEADERS') {
                var headerElement = elements.find(function (elm) {
                    return elm.type === 'primaryHeader' || elm.type === 'secondaryHeader';
                });
                if (headerElement && headerElement.position) {
                    assignElementToCollection(element, headerElement.position, elementsCollections, hasImage);
                }
                else {
                    assignElementToCollection(element, element.position, elementsCollections, hasImage);
                }
            }
            else if (element.position === 'BELOW_SUBHEADER') {
                var subheaderElement = elements.find(function (elm) { return elm.type === 'subheader'; });
                if (subheaderElement && subheaderElement.position) {
                    if (subheaderElement.position === 'BELOW_HEADERS') {
                        var headerElement = elements.find(function (elm) {
                            return elm.type === 'primaryHeader' || elm.type === 'secondaryHeader';
                        });
                        if (headerElement && headerElement.position) {
                            assignElementToCollection(element, headerElement.position, elementsCollections, hasImage);
                        }
                        else {
                            assignElementToCollection(element, element.position, elementsCollections, hasImage);
                        }
                    }
                    else {
                        assignElementToCollection(element, subheaderElement.position, elementsCollections, hasImage);
                    }
                }
                else {
                    assignElementToCollection(element, element.position, elementsCollections, hasImage);
                }
            }
            else if (element && element.position) {
                assignElementToCollection(element, element.position, elementsCollections, hasImage);
            }
            else {
                assignElementToCollection(element, 'TOP', elementsCollections, hasImage);
            }
        });
        return elementsCollections;
    };
    var _k = getContentByPosition(), aboveImage = _k.aboveImage, withinImageTop = _k.withinImageTop, withinImageMiddle = _k.withinImageMiddle, withinImageBottom = _k.withinImageBottom, belowImage = _k.belowImage;
    return (React.createElement(ThemeProvider, { config: settings, sectionType: sectionType },
        React.createElement(Styled.CardContentWrapper, null,
            aboveImage.length > 0 && (React.createElement(Styled.CardTextSetContainer, { key: "TEXTSET_ABOVE_IMAGE", hasImage: false, flexGrow: true, verticalTextPosition: "ABOVE_IMAGE", responsiveSizes: responsiveTextSizes, internalSpace: internalSpace, "$styleQueryMode": styleQueryMode, isTransparent: isTransparent },
                React.createElement(TextSetContent, { elements: aboveImage, verticalAlignment: "ABOVE_IMAGE", inImage: false, backgroundColor: backgroundColor, sectionType: sectionType, ctaLayout: ctaLayout, customBackgroundColor: customBackgroundColor, headerMaxLineLength: headerMaxLineLength, horizontalTextAlignment: horizontalTextAlignment, hoverEffect: hoverEffect, isTransparent: isTransparent, responsiveTextSizes: responsiveTextSizes, cardTextColor: cardTextColor, styleQueryMode: styleQueryMode }))),
            image ? (React.createElement(ImageContent, { isLazy: false, src: image.file.url.includes('http')
                    ? image.file.url
                    : "https:".concat(image.file.url), crop: {
                    width: image.file.details.image.width,
                    height: image.file.details.image.height
                }, maxWidth: image.file.details.image.width, resolution: imageResolutions, alt: image.description, columnSpan: columnSpan },
                React.createElement(Styled.CardTextSetContainer, { hasImage: true, verticalTextPosition: "MIDDLE", flexGrow: true, responsiveSizes: responsiveTextSizes, internalSpace: internalSpace, isTransparent: isTransparent, "$styleQueryMode": styleQueryMode },
                    withinImageTop.length > 0 ? (React.createElement(TextSetContent, { inImage: true, elements: withinImageTop, verticalAlignment: "TOP", backgroundColor: backgroundColor, ctaLayout: ctaLayout, sectionType: sectionType, customBackgroundColor: customBackgroundColor, headerMaxLineLength: headerMaxLineLength, horizontalTextAlignment: horizontalTextAlignment, hoverEffect: hoverEffect, internalSpace: internalSpace, isTransparent: isTransparent, responsiveTextSizes: responsiveTextSizes, cardTextColor: cardTextColor, invertImageTextColor: invertImageTextColor, styleQueryMode: styleQueryMode })) : (React.createElement("div", null)),
                    withinImageMiddle.length > 0 ? (React.createElement(TextSetContent, { inImage: true, elements: withinImageMiddle, verticalAlignment: "MIDDLE", backgroundColor: backgroundColor, ctaLayout: ctaLayout, sectionType: sectionType, customBackgroundColor: customBackgroundColor, headerMaxLineLength: headerMaxLineLength, horizontalTextAlignment: horizontalTextAlignment, hoverEffect: hoverEffect, internalSpace: internalSpace, isTransparent: isTransparent, responsiveTextSizes: responsiveTextSizes, cardTextColor: cardTextColor, invertImageTextColor: invertImageTextColor, styleQueryMode: styleQueryMode })) : (React.createElement("div", null)),
                    withinImageBottom.length > 0 ? (React.createElement(TextSetContent, { inImage: true, elements: withinImageBottom, verticalAlignment: "BOTTOM", backgroundColor: backgroundColor, sectionType: sectionType, ctaLayout: ctaLayout, customBackgroundColor: customBackgroundColor, headerMaxLineLength: headerMaxLineLength, horizontalTextAlignment: horizontalTextAlignment, hoverEffect: hoverEffect, internalSpace: internalSpace, isTransparent: isTransparent, responsiveTextSizes: responsiveTextSizes, cardTextColor: cardTextColor, invertImageTextColor: invertImageTextColor, styleQueryMode: styleQueryMode })) : (React.createElement("div", null))))) : (React.createElement(Styled.CardTextSetContainer, { hasImage: false, verticalTextPosition: "MIDDLE", flexGrow: true, responsiveSizes: responsiveTextSizes, internalSpace: internalSpace, isTransparent: isTransparent, "$styleQueryMode": styleQueryMode },
                withinImageTop.length > 0 ? (React.createElement(TextSetContent, { elements: withinImageTop, verticalAlignment: "TOP", inImage: false, backgroundColor: backgroundColor, sectionType: sectionType, ctaLayout: ctaLayout, customBackgroundColor: customBackgroundColor, headerMaxLineLength: headerMaxLineLength, horizontalTextAlignment: horizontalTextAlignment, hoverEffect: hoverEffect, internalSpace: internalSpace, isTransparent: isTransparent, responsiveTextSizes: responsiveTextSizes, cardTextColor: cardTextColor, styleQueryMode: styleQueryMode })) : (React.createElement("div", null)),
                withinImageMiddle.length > 0 ? (React.createElement(TextSetContent, { elements: withinImageMiddle, verticalAlignment: "MIDDLE", inImage: false, backgroundColor: backgroundColor, sectionType: sectionType, ctaLayout: ctaLayout, customBackgroundColor: customBackgroundColor, headerMaxLineLength: headerMaxLineLength, horizontalTextAlignment: horizontalTextAlignment, hoverEffect: hoverEffect, internalSpace: internalSpace, isTransparent: isTransparent, responsiveTextSizes: responsiveTextSizes, cardTextColor: cardTextColor, styleQueryMode: styleQueryMode })) : (React.createElement("div", null)),
                withinImageBottom.length > 0 ? (React.createElement(TextSetContent, { elements: withinImageBottom, verticalAlignment: "BOTTOM", inImage: false, backgroundColor: backgroundColor, ctaLayout: ctaLayout, sectionType: sectionType, customBackgroundColor: customBackgroundColor, headerMaxLineLength: headerMaxLineLength, horizontalTextAlignment: horizontalTextAlignment, hoverEffect: hoverEffect, internalSpace: internalSpace, isTransparent: isTransparent, responsiveTextSizes: responsiveTextSizes, cardTextColor: cardTextColor, styleQueryMode: styleQueryMode })) : (React.createElement("div", null)))),
            belowImage.length > 0 && (React.createElement(Styled.CardTextSetContainer, { key: "TEXTSET_BELOW_IMAGE", hasImage: false, verticalTextPosition: "BELOW_IMAGE", responsiveSizes: responsiveTextSizes, internalSpace: internalSpace, isTransparent: isTransparent, "$styleQueryMode": styleQueryMode },
                React.createElement(TextSetContent, { elements: belowImage, verticalAlignment: "BELOW_IMAGE", inImage: false, backgroundColor: backgroundColor, ctaLayout: ctaLayout, sectionType: sectionType, customBackgroundColor: customBackgroundColor, headerMaxLineLength: headerMaxLineLength, horizontalTextAlignment: horizontalTextAlignment, hoverEffect: hoverEffect, internalSpace: internalSpace, isTransparent: isTransparent, responsiveTextSizes: responsiveTextSizes, cardTextColor: cardTextColor, styleQueryMode: styleQueryMode }))))));
};
export default Content;
